<script lang="ts">
  import { onMount } from 'svelte';
  import { SceneManager, type LightingPreset, type RenderMode } from '../core/SceneManager';
  import { MotionEngine, isAnimatable } from '../core/MotionEngine';
  import type { Dimension, Groove, SalonType, WvmDocument } from '../lib/types/wvm';
  import { buildPartItems, matchParts, type PartListItem } from '../lib/partItem';
  import { parsePartCode } from '../lib/scan';
  import { formatDims } from '../lib/format';
  import { t, tn } from '../i18n/index.svelte';
  import {
    ApiError,
    putMotion,
    putLighting,
    uploadThumbnail,
    getMeta,
    getMaterials,
    getBandColors,
    listRenders,
    deleteRender,
    type MotionEntry,
  } from '../lib/api/client';
  import type { RenderDto } from '@wood-viewer/shared';
  import logoUrl from '../assets/woodviewer-logo.png';
  import Icon from './Icon.svelte';
  import Toolbar from './Toolbar.svelte';
  import PartList from './PartList.svelte';
  import InfoPanel, { type PartDetail } from './InfoPanel.svelte';
  import MeasureBar from './MeasureBar.svelte';
  import ExplodeBar from './ExplodeBar.svelte';
  import LabelOverlay from './LabelOverlay.svelte';
  import AuthorPanel from './AuthorPanel.svelte';
  import LangSelect from './LangSelect.svelte';
  import ShareDialog from './ShareDialog.svelte';
  import Toast from './Toast.svelte';
  import GestureHints from './GestureHints.svelte';
  import ContextMenu, { type CtxItem } from './ContextMenu.svelte';
  import QrScanner from './QrScanner.svelte';
  import AiRenderPanel from './AiRenderPanel.svelte';
  import RenderGallery from './RenderGallery.svelte';
  import { DimensionTool, type DimMode } from '../core/DimensionTool';
  import { detectDevice } from '../lib/route';

  let {
    doc,
    token = null,
    authKey = null,
    embed = false,
    airender = false,
    initialPartId = null,
  }: {
    doc: WvmDocument;
    token?: string | null;
    authKey?: string | null;
    embed?: boolean;
    /** ?airender=1 → tự mở panel AI Render (chỉ Author, cần token). */
    airender?: boolean;
    /** ?partID={mã tem} → sau khi model dựng xong, tìm + zoom vào chi tiết như quét QR. */
    initialPartId?: string | null;
  } = $props();

  const isAuthor = $derived(!!authKey && !embed);
  const canShare = $derived(!!token && !embed);
  const shareUrl = $derived(token ? `${location.origin}/v/${token}` : '');

  let canvasEl: HTMLCanvasElement;
  let overlayEl: HTMLCanvasElement;
  // $state: template đọc `scene` (overlay nhãn) → cần phản ứng khi scene dựng xong trong onMount.
  let scene = $state<SceneManager | undefined>(undefined);
  let motion: MotionEngine | undefined;
  let tool: DimensionTool | undefined;

  let items = $state<PartListItem[]>([]);
  let selectedId = $state<number | null>(null);
  let renderMode = $state<RenderMode>('matedge'); // mặc định "Vật liệu + Nét"
  // ánh sáng mức model — đồng bộ từ payload đã lưu trong onMount (vắng = mặc định). Chỉ Author chỉnh + lưu.
  let lighting = $state<LightingPreset>('even');
  let lightsOn = $state(true);
  let lightIntensity = $state(50);
  let isMobile = $state(false);
  let sheetOpen = $state(false);
  let showHints = $state(false); // gợi ý cử chỉ lần đầu (mobile)
  let hiddenIds = $state(new Set<number>()); // chi tiết đang ẩn
  // Nút zoom là TOGGLE: đã zoom vào chi tiết đang chọn → nút đổi thành "Về tổng thể".
  // Reset về false khi đổi chọn / về tổng thể (trong select()).
  let zoomedIn = $state(false);
  let ctxMenu = $state<{ x: number; y: number; partId: number; name: string } | null>(null);
  let searchText = $state(''); // ô tìm kiếm (chia sẻ desktop + mobile, đặt được khi quét QR)
  let scannerOpen = $state(false);
  // Mã vừa quét QR: khi ô tìm kiếm còn đúng mã này → lọc/highlight khớp ĐÚNG code (bỏ qua tên/cụm).
  let scanCode = $state<string | null>(null);
  const scanExact = $derived(scanCode !== null && searchText.trim() === scanCode);
  // Chuỗi quét ở nhánh tìm-cả-chuỗi (sai định dạng): để báo "không tìm thấy" khi 0 kết quả.
  let scanQuery: string | null = null;
  // Deep-link ?partID hoặc scanner in-app quét URL → sau khi khớp DUY NHẤT 1 chi tiết thì chọn luôn
  // (mở bảng thông tin). Quét tem trần / gõ tay: false (giữ nguyên chỉ highlight+zoom). Reset sau mỗi lần.
  let selectOnMatch = false;

  let measureOpen = $state(false);
  let dimMode = $state<DimMode>('off');
  let showTotal = $state(false);
  let dimStatus = $state('');
  // 04b: hiển thị kích thước user dựng (toggle độc lập; nút chỉ hiện khi model có dimensions).
  // Mặc định BẬT khi model có dimensions (đặt trong onMount); false chỉ là giá trị trước khi load.
  let dimsOn = $state(false);
  let hasDims = $state(false);

  // Sơ đồ tách rời THEO CỤM (module): chọn ≥1 cụm → thanh hành động (Tách rời / Ẩn cụm / Ẩn phần còn lại).
  let groupSel = $state(new Set<string>()); // tên các cụm đang chọn
  let explodeOn = $state(false); // đang ở chế độ tách rời cụm đã chọn (cũng cô lập = ẩn phần còn lại)
  let explodeAmount = $state(0.4); // mức tách 0..1
  let explodePrevHidden: Set<number> | null = null; // ảnh chụp hiddenIds trước khi tách → khôi phục khi tắt

  // GĐ2 nhãn "sơ đồ lắp đặt": bật/tắt nhãn tên cho tập đang chọn/tách.
  let labelsOn = $state(false);

  // author + share + toast
  let shareOpen = $state(false);
  let toastMsg = $state('');
  // AI Render (04a): panel tạo ảnh (Author) + gallery ảnh đã lưu (public)
  let aiPanelOpen = $state(false);
  let galleryOpen = $state(false);
  let renders = $state<RenderDto[]>([]);
  let authSalon = $state<SalonType>(0);
  let authGroup = $state(0);
  let authFlip = $state(false);
  let authSwingOut = $state(false);
  let saving = $state(false);
  let dirty = $state(false);
  let blocked = $state(false); // model status=blocked → không cho lưu motion (banner + disable)
  let brandLogoUrl = $state<string | null>(null); // logo thương hiệu designer (11); null = dùng logo mặc định
  // WebGL không dựng được context (tăng tốc phần cứng tắt / GPU blocklist / RDP-VM) → hiện hướng dẫn thay vì sập.
  let webglError = $state(false);
  const edited = new Set<number>();
  let toastTimer: ReturnType<typeof setTimeout> | undefined;
  let thumbnailDone = false; // chụp thumbnail 1 lần/lần mở Author
  let thumbTimer: ReturnType<typeof setTimeout> | undefined;

  const hasAnimatable = $derived(items.some((i) => i.animatable));
  // số nhóm chuyển động kế tiếp CHƯA dùng (max+1) → mặc định cấp nhóm MỚI cho mỗi chi tiết
  // mới gán (mỗi cánh/ngăn tự mở riêng). phụ thuộc `items` để tái tính sau mỗi lần đổi motion.
  const nextFreeGroup = $derived.by(() => {
    void items;
    let max = 0;
    for (const p of doc?.parts ?? []) {
      const g = p.motion?.drawerGroup ?? 0;
      if (g > max) max = g;
    }
    return max + 1;
  });
  // mọi nhóm chuyển động + chi tiết thành viên (để panel hiện "mở cùng với …").
  const motionGroups = $derived.by(() => {
    void items;
    const m = new Map<number, { id: number; name: string }[]>();
    for (const p of doc?.parts ?? []) {
      if (!p.motion?.salonType) continue;
      const g = p.motion?.drawerGroup ?? 0;
      if (g <= 0) continue;
      if (!m.has(g)) m.set(g, []);
      m.get(g)!.push({ id: p.id, name: p.name });
    }
    return [...m.entries()].sort((a, b) => a[0] - b[0]).map(([num, members]) => ({ num, members }));
  });

  // #3: tô hổ phách các chi tiết được tô sáng. Ưu tiên: đang CHỌN CỤM (sơ đồ tách rời) → tô mọi chi
  // tiết trong các cụm đã chọn (để thấy đang chọn gì). Nếu không chọn cụm, author tô CÙNG NHÓM chuyển
  // động với chi tiết đang chọn (>1 thành viên). phụ thuộc items/groupSel.
  $effect(() => {
    void items;
    if (!scene) return;
    if (groupSel.size > 0) {
      scene.setSelectedGroup(scopeIds()); // tô teal như chọn chuột (đồng nhất màu)
      scene.setGroupHighlight(null); // tránh chồng với tô cam nhóm-chuyển-động
      return;
    }
    scene.setSelectedGroup(null);
    if (!isAuthor || selectedId == null || authGroup <= 0) {
      scene.setGroupHighlight(null);
      return;
    }
    const ids = (doc?.parts ?? [])
      .filter((p) => (p.motion?.drawerGroup ?? 0) === authGroup && !!p.motion?.salonType)
      .map((p) => p.id);
    scene.setGroupHighlight(ids.length > 1 ? ids : null);
  });

  // tra cứu vật liệu
  const materials = $derived.by(() => {
    const m = new Map<string, { name: string; color: string }>();
    for (const mat of doc.materials ?? []) m.set(mat.id, { name: mat.name, color: mat.color });
    return m;
  });

  const selectedDetail = $derived.by<PartDetail | null>(() => {
    if (selectedId == null) return null;
    const p = doc.parts.find((x) => x.id === selectedId);
    if (!p) return null;
    const mat = p.materialId ? materials.get(p.materialId) : undefined;
    return {
      name: p.name,
      code: p.code,
      dims: formatDims(p),
      materialName: mat?.name,
      materialColor: mat?.color,
      motion: (p.motion?.salonType ?? 0) as number,
    };
  });

  // ----- hành động -----
  function select(id: number | null) {
    if (id != null && groupSel.size > 0) {
      if (explodeOn) stopExplode(); // click chi tiết đơn → rời chế độ chọn-cụm/tách
      groupSel = new Set();
    }
    selectedId = id;
    zoomedIn = false; // đổi chọn (hoặc bỏ chọn) → nút zoom về lại trạng thái "zoom vào"
    scene?.setSelected(id);
    if (isAuthor && id != null) {
      const p = doc.parts.find((x) => x.id === id);
      authSalon = (p?.motion?.salonType ?? 0) as SalonType;
      authFlip = p?.motion?.flip ?? false;
      authGroup = p?.motion?.drawerGroup ?? 0;
      authSwingOut = p?.motion?.swingOut ?? false;
    }
  }
  function selectFromList(id: number) {
    // đang đo lọt lòng → bấm từ danh sách để chọn tấm (tránh tấm bị che trên 3D)
    if (dimMode === 'inner' && tool) {
      const r = tool.pickInnerById(id);
      if (r && r.kind === 'inner') dimStatus = r.status;
      select(id);
      return;
    }
    select(id);
    scene?.focusPart(id);
  }
  function toggle(id: number) {
    motion?.toggle(id);
  }
  function toggleVisible(id: number) {
    const next = new Set(hiddenIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    hiddenIds = next;
    scene?.setPartVisible(id, !next.has(id));
  }
  /** Hiện lại TOÀN BỘ chi tiết đang ẩn (một chạm) — nút bổ sung, không thay toggle từng tấm. */
  function showAll() {
    for (const id of hiddenIds) scene?.setPartVisible(id, true);
    hiddenIds = new Set();
  }
  /** Toggle: chưa zoom → fit vào chi tiết đang chọn (nút đổi thành "Về tổng thể");
   *  đã zoom → về phối cảnh tổng thể (resetView tự đặt zoomedIn=false qua select(null)). */
  function toggleZoomSelected() {
    if (zoomedIn) {
      resetView();
      return;
    }
    const ids = selectionIds(); // zoom tới chi tiết đơn HOẶC toàn bộ chi tiết trong cụm đã chọn
    if (ids.length === 0) return;
    scene?.fitToParts(ids, isMobile ? 0.5 : undefined);
    zoomedIn = true;
  }
  /** Isolate: ẩn MỌI chi tiết trừ `id` (chỉ còn `id` hiện). Khôi phục bằng nút "Hiện lại toàn bộ". */
  function isolatePart(id: number) {
    const next = new Set<number>();
    for (const p of doc.parts) {
      const vis = p.id === id;
      scene?.setPartVisible(p.id, vis);
      if (!vis) next.add(p.id);
    }
    hiddenIds = next;
    select(id); // chọn luôn chi tiết được giữ lại (bảng thông tin + highlight)
  }
  /** Ẩn/hiện cả một cụm: tất cả đang ẩn → hiện hết; còn lại → ẩn hết. */
  function toggleModuleVisible(ids: number[]) {
    if (ids.length === 0) return;
    const show = ids.every((id) => hiddenIds.has(id)); // cụm đang ẩn hết → chuyển sang hiện
    const next = new Set(hiddenIds);
    for (const id of ids) {
      if (show) next.delete(id);
      else next.add(id);
      scene?.setPartVisible(id, show);
    }
    hiddenIds = next;
  }

  // ----- tìm kiếm: highlight + zoom gọn vào kết quả -----
  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  function handleSearch(matchedIds: number[], hasQuery: boolean) {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      if (!hasQuery) {
        scene?.setSearchHighlight(null); // xóa từ khóa → bỏ highlight
        scanCode = null;
        scanQuery = null;
        selectOnMatch = false;
        return;
      }
      // matchedIds từ PartList đã code-only khi quét QR (prop exact) → dùng trực tiếp.
      if (matchedIds.length === 0) {
        // Quét cả-chuỗi (sai định dạng) mà không có chi tiết nào khớp → báo cho người dùng.
        if (scanQuery !== null && searchText.trim() === scanQuery) {
          showToast(t('scan.notFound', { text: scanQuery }));
        }
        scanQuery = null;
        selectOnMatch = false;
        return; // không khớp → giữ nguyên view
      }
      scanQuery = null;
      scene?.setSearchHighlight(matchedIds);
      // mobile: không zoom quá sát → giới hạn bề ngang chi tiết ≤ 1/2 màn hình
      scene?.fitToParts(matchedIds, isMobile ? 0.5 : undefined);
      // Deep-link/scanner-URL: khớp đúng 1 chi tiết → chọn luôn (mở bảng thông tin). Nhiều kết quả →
      // không chọn (không rõ chi tiết nào), chỉ highlight+zoom như thường.
      if (selectOnMatch && matchedIds.length === 1) select(matchedIds[0]);
      selectOnMatch = false;
    }, 250);
  }

  // Nguồn DUY NHẤT lái highlight + zoom theo từ khóa. Đặt Ở VIEWER (không trong PartList) để hoạt
  // động cả khi PartList CHƯA mount — trên mobile PartList nằm trong sheet đóng ({#if sheetOpen}) nên
  // deep-link ?partID / quét QR trước đây không zoom được. matchParts là hàm thuần, khớp y hệt PartList.
  $effect(() => {
    const q = searchText.trim();
    const ids = q ? matchParts(items, searchText, scanExact).map((it) => it.id) : [];
    handleSearch(ids, q.length > 0);
  });

  // ----- về trạng thái ban đầu: phối cảnh tổng thể, xóa highlight/chọn/ô tìm -----
  const focused = $derived(searchText.trim() !== '' || selectedId != null);
  function resetView() {
    searchText = ''; // xóa ô tìm (→ bỏ highlight) + reset cờ quét
    scanCode = null;
    scene?.setSearchHighlight(null); // chắc chắn bỏ làm mờ kể cả khi search debounce chưa chạy
    select(null); // bỏ chọn (setSelected(null))
    sheetOpen = false; // mobile: thu gọn panel "Chi tiết mô hình" về như ban đầu
    scene?.fitView(); // phối cảnh iso + vừa khung toàn model
  }
  // Desktop: Esc cũng về tổng thể (khi đang focus, không có dialog/scanner/measure mở).
  function onWindowKey(e: KeyboardEvent) {
    if (
      e.key === 'Escape' &&
      !embed &&
      !isMobile &&
      focused &&
      !shareOpen &&
      !scannerOpen &&
      !measureOpen &&
      !ctxMenu
    ) {
      resetView();
    }
  }

  // ----- quét mã chi tiết (QR) -----
  function openScanner() {
    scannerOpen = true;
  }
  function handleScanResult(query: string, exact = true, selectUnique = false) {
    scannerOpen = false;
    scanCode = exact ? query : null; // exact → khớp ĐÚNG mã; else → tìm thường theo cả chuỗi
    // Theo dõi 0-kết-quả cho CẢ HAI nhánh: tem parse được nhưng không có trong model (WVM cũ thiếu
    // designation, hoặc tem của model khác) trước đây im lặng → user thấy list trống, không rõ vì sao.
    scanQuery = query.trim();
    // Nguồn URL (deep-link ?partID / scanner in-app đọc URL) → chọn luôn chi tiết khi khớp duy nhất.
    selectOnMatch = selectUnique;
    searchText = query; // điền vào ô tìm kiếm (hiển thị) → kích hoạt highlight + zoom (+ select nếu duy nhất)
  }

  // Deep-link ?partID={mã tem}: chạy 1 lần sau khi model dựng xong. Dùng lại ĐÚNG đường quét QR
  // (parse tem → khớp designation → highlight + zoom-fit; không trúng → toast scan.notFound).
  function applyInitialPartId() {
    const raw = initialPartId?.trim();
    if (!raw) return;
    const code = parsePartCode(raw);
    // selectUnique=true: deep-link luôn từ URL → khớp duy nhất thì chọn luôn chi tiết.
    if (code != null) handleScanResult(code, true, true);
    else handleScanResult(raw, false, true); // không tách được mã → tìm cả-chuỗi (như QR sai định dạng)
    // Gỡ partID khỏi URL: Esc/reset về tổng thể sạch, F5 không ép zoom lại. Giữ nguyên phần còn lại.
    try {
      const url = new URL(location.href);
      const key = [...url.searchParams.keys()].find((k) => k.toLowerCase() === 'partid');
      if (key) {
        url.searchParams.delete(key);
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
    } catch {
      /* URL API không dùng được → bỏ qua, không ảnh hưởng chức năng */
    }
  }

  // ----- context menu (chuột phải / nhấn giữ) -----
  function openContextAt(clientX: number, clientY: number) {
    const hit = scene?.raycast(clientX, clientY);
    if (!hit) {
      ctxMenu = null;
      return;
    }
    ctxMenu = { x: clientX, y: clientY, partId: hit.part.id, name: hit.part.name };
  }
  const ctxItems = $derived.by<CtxItem[]>(() => {
    if (!ctxMenu) return [];
    const id = ctxMenu.partId;
    const p = doc.parts.find((x) => x.id === id);
    const arr: CtxItem[] = [];
    if (p && isAnimatable(p)) {
      arr.push({
        label: motion?.isOpen(id) ? t('ctx.close') : t('ctx.open'),
        icon: 'slide',
        onClick: () => toggle(id),
      });
    }
    arr.push({ label: t('ctx.hide'), icon: 'eyeOff', danger: true, onClick: () => toggleVisible(id) });
    arr.push({ label: t('ctx.isolate'), icon: 'isolate', onClick: () => isolatePart(id) });
    return arr;
  });
  function setRenderMode(m: RenderMode) {
    renderMode = m;
    scene?.setRenderMode(m);
  }
  // ánh sáng — chỉ Author chỉnh; mọi thay đổi đánh dấu dirty để bật "Lưu thay đổi".
  function setLighting(p: LightingPreset) {
    lighting = p;
    scene?.setLighting(p);
    dirty = true;
  }
  function toggleLights() {
    lightsOn = !lightsOn;
    scene?.setLightsOn(lightsOn);
    dirty = true;
  }
  function setLightIntensity(v: number) {
    lightIntensity = v;
    if (v > 0 && !lightsOn) {
      lightsOn = true; // kéo sáng lên khi đang tắt → tự bật lại
      scene?.setLightsOn(true);
    }
    scene?.setLightIntensity(v);
    dirty = true;
  }

  // ----- đo đạc -----
  function toggleMeasure() {
    measureOpen = !measureOpen;
    if (!measureOpen) closeMeasure();
    else if (explodeOn) stopExplode(); // 1 thanh nổi tại 1 thời điểm → tắt sơ đồ tách rời
  }
  function closeMeasure() {
    measureOpen = false;
    tool?.setMode('off');
    dimMode = 'off';
    dimStatus = '';
  }
  function setDimMode(m: DimMode) {
    tool?.setMode(m);
    dimMode = m;
    dimStatus = m === 'inner' ? t('measure.innerHint') : '';
  }
  function toggleTotal() {
    showTotal = tool?.toggleTotal() ?? false;
  }
  function clearDims() {
    tool?.clearAll();
    dimStatus = '';
  }
  // 04b: bật/tắt kích thước user dựng (độc lập công cụ đo; overlay 2D repaint ngay trong setAuthoredVisible).
  function toggleDims() {
    dimsOn = !dimsOn;
    tool?.setAuthoredVisible(dimsOn);
  }

  // ----- lựa chọn (chi tiết đơn HOẶC cụm) + sơ đồ tách rời -----
  /** id các chi tiết thuộc những cụm đang chọn. */
  function scopeIds(): number[] {
    if (groupSel.size === 0) return [];
    return items.filter((it) => groupSel.has(it.module)).map((it) => it.id);
  }
  /** Lựa chọn hiện tại: ưu tiên CỤM; nếu không có cụm thì là chi tiết đơn đang chọn (click). */
  function selectionIds(): number[] {
    if (groupSel.size > 0) return scopeIds();
    return selectedId != null ? [selectedId] : [];
  }
  /** Chọn/bỏ 1 cụm. Chọn cụm → BỎ chọn chi tiết đơn (chuyển hẳn sang chọn theo cụm). */
  function toggleGroupSel(module: string) {
    const next = new Set(groupSel);
    if (next.has(module)) next.delete(module);
    else next.add(module);
    groupSel = next;
    if (next.size > 0 && selectedId != null) {
      selectedId = null; // chuyển sang chọn cụm → bỏ chi tiết đơn đã chọn trước đó
      scene?.setSelected(null);
    }
    zoomedIn = false;
    if (next.size === 0) {
      if (explodeOn) stopExplode();
    } else if (explodeOn) {
      applyExplodeOn();
    }
  }
  /** Bỏ TOÀN BỘ lựa chọn (cụm + chi tiết đơn) + tắt tách. */
  function clearSelection() {
    if (explodeOn) stopExplode();
    groupSel = new Set();
    select(null);
  }
  /** Chọn TẤT CẢ cụm (ô check trên cùng) — truyền danh sách tên cụm; null = bỏ chọn hết. */
  function selectAllGroups(modules: string[] | null) {
    if (!modules) {
      clearSelection();
      return;
    }
    if (selectedId != null) {
      selectedId = null; // chuyển sang chọn cụm → bỏ chi tiết đơn
      scene?.setSelected(null);
    }
    groupSel = new Set(modules);
    zoomedIn = false;
    if (explodeOn) applyExplodeOn();
  }
  /** Áp chế độ tách: chỉ chi tiết đã chọn hiện + nổ quanh tâm riêng; phần còn lại ẩn. */
  function applyExplodeOn() {
    const scope = selectionIds();
    const inScope = new Set(scope);
    const hidden = new Set<number>();
    for (const p of doc.parts) {
      const vis = inScope.has(p.id);
      scene?.setPartVisible(p.id, vis);
      if (!vis) hidden.add(p.id);
    }
    hiddenIds = hidden;
    scene?.setExplode(explodeAmount, scope);
  }
  /** Nút Explode: bật/tắt tách rời. Chỉ bật khi lựa chọn có ≥2 chi tiết. */
  function toggleExplode() {
    if (explodeOn) {
      stopExplode();
      return;
    }
    if (selectionIds().length < 2) return;
    if (measureOpen) closeMeasure();
    explodePrevHidden = new Set(hiddenIds); // nhớ ẩn thủ công để khôi phục đúng khi tắt
    explodeOn = true;
    applyExplodeOn();
  }
  /** Tắt tách: lắp ráp lại + hiện lại ĐÚNG trạng thái ẩn trước khi tách (không đụng ẩn thủ công). */
  function stopExplode() {
    explodeOn = false;
    scene?.setExplode(0);
    const prev = explodePrevHidden ?? new Set<number>();
    for (const p of doc.parts) scene?.setPartVisible(p.id, !prev.has(p.id));
    hiddenIds = new Set(prev);
    explodePrevHidden = null;
  }
  function setExplodeAmount(v: number) {
    explodeAmount = v;
    scene?.setExplode(v, selectionIds());
  }
  /** Ẩn các chi tiết đang chọn (giữ phần còn lại). */
  function hideSelGroups() {
    const next = new Set(hiddenIds);
    for (const id of selectionIds()) {
      next.add(id);
      scene?.setPartVisible(id, false);
    }
    hiddenIds = next;
  }
  /** Ẩn phần còn lại = cô lập các chi tiết đang chọn (lắp ráp, KHÔNG nổ). */
  function isolateSelGroups() {
    const inScope = new Set(selectionIds());
    const next = new Set<number>();
    for (const p of doc.parts) {
      const vis = inScope.has(p.id);
      scene?.setPartVisible(p.id, vis);
      if (!vis) next.add(p.id);
    }
    hiddenIds = next;
  }

  // ----- nhãn sơ đồ lắp đặt (GĐ2) -----
  /** Danh sách nhãn = tên các chi tiết trong lựa chọn hiện tại (khi bật nhãn). */
  const labelItems = $derived.by<{ id: number; text: string }[]>(() => {
    if (!labelsOn) return [];
    const ids = new Set(selectionIds());
    if (ids.size === 0) return [];
    return items.filter((it) => ids.has(it.id)).map((it) => ({ id: it.id, text: it.name }));
  });
  function toggleLabels() {
    labelsOn = !labelsOn;
    if (!labelsOn) scene?.setHover(null);
  }
  function onLabelHover(id: number | null) {
    scene?.setHover(id);
  }
  /** Click nhãn: zoom/ngắm chi tiết đó NHƯNG giữ nguyên lựa chọn + dàn nhãn (không gọi select). */
  function onLabelClick(id: number) {
    scene?.fitToParts([id], isMobile ? 0.5 : undefined);
    zoomedIn = true;
  }

  // ----- author -----
  function showToast(msg: string) {
    toastMsg = msg;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (toastMsg = ''), 2200);
  }
  function applyMotion(st: SalonType) {
    if (selectedId == null || blocked) return;
    // #1: chi tiết CHƯA có nhóm → cấp nhóm MỚI (mở riêng) thay vì dính nhóm cũ; giữ nhóm nếu đã có.
    const dg = st ? (authGroup > 0 ? authGroup : nextFreeGroup) : 0;
    authSalon = st;
    authGroup = dg;
    motion?.reassign(selectedId, st, dg, authFlip, authSwingOut);
    edited.add(selectedId);
    dirty = true;
    items = buildPartItems(doc);
    motion?.previewOpen(selectedId);
  }
  function clearMotion() {
    if (selectedId == null) return;
    authSalon = 0;
    authGroup = 0;
    authFlip = false;
    authSwingOut = false;
    motion?.reassign(selectedId, 0, 0, false, false);
    edited.add(selectedId);
    dirty = true;
    items = buildPartItems(doc);
  }
  // gán chi tiết đang chọn sang nhóm `g` (giữ nguyên loại chuyển động + đảo chiều).
  function setGroupTo(g: number) {
    if (selectedId == null || !authSalon || blocked) return;
    authGroup = g;
    motion?.reassign(selectedId, authSalon, g, authFlip, authSwingOut);
    edited.add(selectedId);
    dirty = true;
    items = buildPartItems(doc);
  }
  // #2: tách ra nhóm RIÊNG (mở một mình) = chuyển sang một số nhóm mới chưa dùng.
  function setIndependent() {
    setGroupTo(nextFreeGroup);
  }
  // #2: gộp vào nhóm có sẵn (mở cùng các thành viên nhóm đó).
  function mergeIntoGroup(g: number) {
    setGroupTo(g);
  }
  function toggleFlip() {
    if (selectedId == null) return;
    authFlip = !authFlip;
    motion?.reassign(selectedId, authSalon, authGroup, authFlip, authSwingOut);
    edited.add(selectedId);
    dirty = true;
    motion?.previewOpen(selectedId);
  }
  // Chiều mở ra/vào (depth) — TRỰC GIAO với flip (trái/phải). Chỉ có nghĩa cho cửa bản lề đứng (2/3).
  function toggleSwingOut() {
    if (selectedId == null || (authSalon !== 2 && authSalon !== 3)) return;
    authSwingOut = !authSwingOut;
    motion?.reassign(selectedId, authSalon, authGroup, authFlip, authSwingOut);
    edited.add(selectedId);
    dirty = true;
    motion?.previewOpen(selectedId);
  }
  async function save() {
    if (!token || !authKey) return;
    if (blocked) {
      showToast(t('toast.locked'));
      return;
    }
    saving = true;
    const entries: MotionEntry[] = [...edited]
      .map((id) => doc.parts.find((p) => p.id === id))
      .filter((p): p is NonNullable<typeof p> => !!p)
      .map((p) => ({
        partId: p.id,
        salonType: (p.motion?.salonType ?? 0) as SalonType,
        drawerGroup: p.motion?.drawerGroup ?? 0,
        flip: p.motion?.flip ?? false,
        swingOut: p.motion?.swingOut ?? false,
      }));
    try {
      await putMotion(token, authKey, entries);
      // ánh sáng mức model (design 03h). Backend chưa deploy endpoint → 404/405: bỏ qua để
      // không chặn lưu motion (sẽ tự hoạt động khi backend sẵn sàng).
      try {
        await putLighting(token, authKey, { preset: lighting, on: lightsOn, intensity: lightIntensity });
      } catch (e) {
        if (!(e instanceof ApiError && (e.status === 404 || e.status === 405))) throw e;
      }
      thumbnailDone = true; // lưu sẽ tự chụp lại → khỏi auto-capture nữa
      await uploadThumb();
      dirty = false;
      edited.clear();
      showToast(t('toast.saved'));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) showToast(t('toast.badEditLink'));
      else if (e instanceof ApiError && e.status === 409) showToast(t('toast.locked'));
      else showToast(t('toast.saveFailed'));
    }
    saving = false;
  }

  // ----- thumbnail -----
  /** Chụp canvas → upload thumbnail (authKey tùy chọn). Lỗi im lặng, không chặn trải nghiệm. */
  async function uploadThumb() {
    if (!token || !scene) return;
    try {
      await uploadThumbnail(token, authKey, await scene.captureThumbnail());
    } catch {
      /* thumbnail không chặn xem/sửa */
    }
  }
  /** Tự chụp 1 lần sau khi scene/camera ổn định — mọi chế độ (kể cả khách), trừ embed. */
  function autoCaptureThumbnail() {
    if (thumbnailDone || !token || embed) return;
    thumbnailDone = true;
    uploadThumb();
  }

  // ----- AI Render (04a) -----
  /** Tải danh sách ảnh render đã lưu (public) → quyết định hiện nút gallery. Lỗi im lặng. */
  async function refreshRenders() {
    if (!token || embed) return;
    try {
      renders = await listRenders(token, authKey);
    } catch {
      /* gallery không chặn trải nghiệm xem */
    }
  }
  function captureHighRes(width: number, height: number): Promise<Blob> {
    if (!scene) return Promise.reject(new Error('scene chưa sẵn sàng'));
    return scene.captureHighRes(width, height);
  }
  // 04a: pass cấu trúc (line-pass) đồng trục camera với ảnh màu — guide multi-image.
  function captureGuide(width: number, height: number): Promise<Blob> {
    if (!scene) return Promise.reject(new Error('scene chưa sẵn sàng'));
    return scene.captureGuide(width, height, 'line');
  }
  async function deleteRenderFromGallery(id: number) {
    if (!token || !authKey) return;
    try {
      await deleteRender(token, authKey, id);
      renders = renders.filter((r) => r.id !== id);
      showToast(t('airender.toastDeleted'));
    } catch {
      showToast(t('airender.errGeneric'));
    }
  }
  // Author: đặt 1 ảnh render trong gallery làm ảnh đại diện (thumbnail) của model.
  let settingThumb = false;
  async function setGalleryThumbnail(r: RenderDto) {
    if (!token || !authKey || !r.imageUrl || settingThumb) return;
    settingThumb = true;
    try {
      const res = await fetch(r.imageUrl);
      const blob = await res.blob();
      await uploadThumbnail(token, authKey, blob);
      showToast(t('airender.toastThumbnail'));
    } catch {
      showToast(t('airender.errGeneric'));
    }
    settingThumb = false;
  }

  // ----- share / embed -----
  function openShare() {
    shareOpen = true;
  }
  function toggleFullscreen() {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  }
  function dismissHints() {
    showHints = false;
    try {
      localStorage.setItem('wv_hints_seen', '1');
    } catch {
      /* localStorage không khả dụng */
    }
  }

  onMount(() => {
    try {
      // Render an toàn cho di động: mặc định BẬT trên mọi thiết bị mobile (vá crash WebGL ở model nặng).
      // CHỦ model có thể TẮT qua Console → meta.mobileSafeRender=false → nâng lại chất lượng đầy đủ (dưới, sau khi meta về).
      scene = new SceneManager(canvasEl, { mobileSafe: detectDevice() === 'mobile' });
    } catch (e) {
      // WebGL context không tạo được → không dựng scene, hiện overlay hướng dẫn. Cleanup bên dưới an toàn với scene=undefined.
      webglError = true;
      console.error('[viewer] WebGL init failed', e);
      return;
    }
    const objs = scene.buildModel(doc);
    // áp ánh sáng đã lưu (mọi người xem); vắng → giữ mặc định even/on/50.
    if (doc.lighting) {
      lighting = doc.lighting.preset;
      lightsOn = doc.lighting.on;
      lightIntensity = doc.lighting.intensity;
    }
    // Người xem (không phải tác giả) mặc định TẮT chiếu sáng → mẫu hiện phẳng, không bóng; bật lại
    // bằng nút Chiếu sáng trên toolbar (dùng preset+cường độ tác giả đã lưu). Embed KHÔNG có toolbar
    // nên giữ nguyên trạng thái tác giả đã lưu. Tác giả giữ trạng thái đã lưu để AuthorPanel đúng.
    if (!isAuthor && !embed) lightsOn = false;
    scene.applyLightingState({ preset: lighting, on: lightsOn, intensity: lightIntensity });
    motion = new MotionEngine(objs, () => scene!.addAnimator(motion!.update));
    // Dev-only test hook (loại khỏi bản prod nhờ import.meta.env.DEV): cho scripts/verify-*.mjs
    // soi trạng thái texture (04d) và mô phỏng quét QR (verify khớp code chính xác).
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__scene = scene;
      (window as unknown as Record<string, unknown>).__parts = objs;
      (window as unknown as Record<string, unknown>).__scan = handleScanResult;
      // verify-*.mjs: nạp gallery với render giả + mở (test nút "đặt thumbnail" offline).
      (window as unknown as Record<string, unknown>).__openGalleryWith = (rs: RenderDto[]) => {
        renders = rs;
        galleryOpen = true;
      };
      // 04b: nạp dimensions mẫu để xem thử khi chưa có plugin/backend (hiện nút + bật luôn).
      (window as unknown as Record<string, unknown>).__setDims = (d: Dimension[]) => {
        tool?.setAuthoredDims(d);
        hasDims = d.length > 0;
        dimsOn = d.length > 0;
        tool?.setAuthoredVisible(dimsOn);
      };
      // 04c: nạp rãnh xoi mẫu cho 1 part để xem thử khi chưa có plugin/backend.
      (window as unknown as Record<string, unknown>).__setGrooves = (partId: number, g: Groove[]) => {
        scene?.setPartGrooves(partId, g);
      };
    }
    scene.invalidate();
    items = buildPartItems(doc);

    // Deep-link ?partID={mã tem}: tìm + zoom vào chi tiết ngay khi model sẵn sàng (items đã dựng
    // → PartList khớp được; fitToParts chạy sau debounce 250ms nên không bị fitView ban đầu ghi đè).
    applyInitialPartId();

    // texture vật liệu (04d) — resolve động ở server, progressive swap khi ảnh tải xong.
    // Lỗi resolve/tải → giữ màu trơn (không chặn render). Demo (không token) → bỏ qua.
    if (token) {
      getMaterials(token, authKey)
        .then((m) => scene?.applyTextures(m))
        .catch(() => {});
      // 04a: màu nẹp đã lưu (public, share-link ẩn danh) → áp name→id→mesh. Vắng → giữ mặc định #CFCFCF.
      getBandColors(token, authKey)
        .then((bc) => scene?.applyBandColors(bc))
        .catch(() => {});
    }

    // overlay đo đạc: vẽ lại sau mỗi lần render WebGL
    tool = new DimensionTool(overlayEl, scene);
    scene.onAfterRender(tool.draw);
    tool.setAuthoredDims(doc.dimensions ?? []); // 04b: kích thước user dựng (vắng = không có)
    hasDims = (doc.dimensions?.length ?? 0) > 0;
    dimsOn = hasDims; // mặc định BẬT kích thước khi model có (người xem tắt bằng nút nếu muốn)
    tool.setAuthoredVisible(dimsOn);

    // Tự chụp + upload thumbnail 1 lần sau khi scene/camera ổn định (mọi chế độ, trừ embed)
    if (token && !embed) {
      thumbTimer = setTimeout(autoCaptureThumbnail, 600);
    }

    // Meta công khai: logo thương hiệu (mọi mode có header) + trạng thái khóa (chỉ Author).
    // Header .brand hiển thị {#if !embed} nên chỉ cần fetch khi !embed.
    if (token && !embed) {
      getMeta(token, authKey)
        .then((m) => {
          brandLogoUrl = m.brandLogoUrl ?? null;
          if (isAuthor) blocked = m.status === 'blocked';
          // Chủ model TẮT render an toàn mobile → khôi phục chất lượng đầy đủ (chỉ tác động khi đang ở mobile).
          if (m.mobileSafeRender === false && detectDevice() === 'mobile') scene?.setMobileSafe(false);
        })
        .catch(() => {});
    }

    // AI Render: nạp gallery ảnh đã lưu (mọi chế độ trừ embed) + tự mở panel khi ?airender=1 (Author)
    if (token && !embed) {
      refreshRenders();
      if (airender && isAuthor) aiPanelOpen = true;
    }

    // tương tác canvas: tap chọn · double-tap mở/đóng · chuột phải/nhấn giữ → menu
    let downX = 0;
    let downY = 0;
    let moved = false;
    let lastTap = 0;
    let lastX = 0;
    let lastY = 0;
    let longTimer: ReturnType<typeof setTimeout> | undefined;
    let longPressed = false;
    const clearLong = () => {
      clearTimeout(longTimer);
      longTimer = undefined;
    };

    const onDown = (e: PointerEvent) => {
      downX = e.clientX;
      downY = e.clientY;
      moved = false;
      longPressed = false;
      clearLong();
      // mobile: nhấn giữ ~500ms (không di) → context menu
      if (e.pointerType === 'touch' && !embed) {
        longTimer = setTimeout(() => {
          if (!moved) {
            longPressed = true;
            openContextAt(downX, downY);
          }
        }, 500);
      }
    };
    const onMove = (e: PointerEvent) => {
      if (Math.hypot(e.clientX - downX, e.clientY - downY) > 6) {
        moved = true;
        clearLong();
      }
      tool?.hover(e.clientX, e.clientY);
    };
    const onUp = (e: PointerEvent) => {
      clearLong();
      if (e.button !== 0) return; // bỏ qua chuột phải/giữa (đã có contextmenu)
      if (longPressed) {
        longPressed = false;
        return;
      }
      if (moved) return;
      // chế độ đo: tap nạp điểm/chọn tấm, không chọn part
      if (tool && tool.mode !== 'off') {
        const r = tool.pick(e.clientX, e.clientY);
        if (r) {
          dimStatus =
            r.kind === 'manual'
              ? r.mm != null
                ? t('measure.distance', { mm: r.mm })
                : t('measure.point1')
              : r.status;
        }
        return;
      }
      const hit = scene!.raycast(e.clientX, e.clientY);
      const now = performance.now();
      const isDouble =
        now - lastTap < 320 && Math.hypot(e.clientX - lastX, e.clientY - lastY) < 24;
      lastTap = now;
      lastX = e.clientX;
      lastY = e.clientY;
      if (isDouble) {
        if (hit) toggle(hit.part.id);
      } else {
        select(hit ? hit.part.id : null);
      }
    };

    // desktop: chuột phải → context menu
    const onContext = (e: MouseEvent) => {
      if (embed) return;
      e.preventDefault();
      openContextAt(e.clientX, e.clientY);
    };

    canvasEl.addEventListener('pointerdown', onDown);
    canvasEl.addEventListener('pointermove', onMove);
    canvasEl.addEventListener('pointerup', onUp);
    canvasEl.addEventListener('contextmenu', onContext);

    const mq = window.matchMedia('(max-width: 767px)');
    const onMq = () => (isMobile = mq.matches);
    onMq();
    mq.addEventListener('change', onMq);

    // gợi ý cử chỉ lần đầu trên mobile (1 lần/trình duyệt)
    try {
      if (isMobile && !embed && !localStorage.getItem('wv_hints_seen')) showHints = true;
    } catch {
      /* localStorage không khả dụng */
    }

    return () => {
      clearTimeout(thumbTimer);
      clearTimeout(searchTimer);
      clearLong();
      canvasEl.removeEventListener('pointerdown', onDown);
      canvasEl.removeEventListener('pointermove', onMove);
      canvasEl.removeEventListener('pointerup', onUp);
      canvasEl.removeEventListener('contextmenu', onContext);
      mq.removeEventListener('change', onMq);
      scene?.dispose();
    };
  });
</script>

<svelte:window onkeydown={onWindowKey} />

<div class="viewer">
  <canvas bind:this={canvasEl}></canvas>
  <canvas class="dim-overlay" bind:this={overlayEl}></canvas>

  {#if labelsOn && !embed && scene && labelItems.length > 0}
    <LabelOverlay {scene} items={labelItems} onHover={onLabelHover} onClick={onLabelClick} />
  {/if}

  {#if webglError}
    <div class="webgl-fallback" role="alert">
      <div class="webgl-card">
        <Icon name="warning" size={40} />
        <h2>{t('err.webgl.title')}</h2>
        <p>{t('err.webgl.body')}</p>
        <button class="retry-btn" onclick={() => location.reload()}>{t('app.retry')}</button>
      </div>
    </div>
  {/if}

  {#if !embed}
    <header class="brand">
      <!-- Logo thương hiệu designer (11) nếu có; lỗi tải → về logo mặc định. Embed-mark bên dưới GIỮ Wood Viewer. -->
      <img
        class:brand-custom={!!brandLogoUrl}
        src={brandLogoUrl ?? logoUrl}
        alt="Wood Viewer"
        onerror={() => {
          if (brandLogoUrl) brandLogoUrl = null;
        }}
      />
    </header>
    <div class="title-chip">
      <strong>{doc.model.name}</strong>
      <span>{tn('parts.count', items.length)}</span>
    </div>
    {#if isAuthor}
      {#if blocked}
        <div class="author-badge locked">🔒 {t('author.lockedBadge')}</div>
      {:else}
        <div class="author-badge"><Icon name="save" size={14} /> {t('author.editBadge')}</div>
      {/if}
    {/if}
    <div class="topright">
      <LangSelect />
      {#if !embed && renders.length > 0}
        <button class="share-btn" title={t('airender.galleryBtn')} onclick={() => (galleryOpen = true)}>
          <Icon name="image" size={18} /><span>{t('airender.galleryBtn')}</span>
        </button>
      {/if}
      {#if canShare}
        <button class="share-btn" title={t('common.share')} onclick={openShare}>
          <Icon name="share" size={18} /><span>{t('common.share')}</span>
        </button>
      {/if}
    </div>

    <Toolbar
      {renderMode}
      {hasAnimatable}
      measureActive={measureOpen}
      {lightsOn}
      {hasDims}
      {dimsOn}
      onRenderMode={setRenderMode}
      onView={(p) => scene?.viewPreset(p)}
      onOpenAll={() => motion?.openAll()}
      onCloseAll={() => motion?.closeAll()}
      onMeasure={toggleMeasure}
      onToggleLights={toggleLights}
      onToggleDims={toggleDims}
    />
  {:else}
    <div class="embed-bar">
      {#if hasAnimatable}
        <button title={t('tb.openAll')} aria-label={t('tb.openAll')} onclick={() => motion?.openAll()}>
          <Icon name="expand" />
        </button>
        <button title={t('tb.closeAll')} aria-label={t('tb.closeAll')} onclick={() => motion?.closeAll()}>
          <Icon name="collapse" />
        </button>
      {/if}
      <button title={t('tb.fit')} aria-label={t('tb.fit')} onclick={() => scene?.fitView()}>
        <Icon name="fit" />
      </button>
      <button title={t('tb.fullscreen')} aria-label={t('tb.fullscreen')} onclick={toggleFullscreen}>
        <Icon name="fullscreen" />
      </button>
    </div>
    <a class="embed-mark" href={shareUrl || '#'} target="_blank" rel="noopener">
      <img src={logoUrl} alt="Wood Viewer" />
    </a>
  {/if}

  {#if measureOpen && !embed}
    <MeasureBar
      mode={dimMode}
      {showTotal}
      status={dimStatus}
      onMode={setDimMode}
      onToggleTotal={toggleTotal}
      onClear={clearDims}
      onClose={closeMeasure}
    />
  {/if}

  {#if explodeOn && !embed}
    <ExplodeBar
      amount={explodeAmount}
      scopeCount={groupSel.size}
      onAmount={setExplodeAmount}
      onClose={stopExplode}
    />
  {/if}

  {#if !embed && !isMobile}
    <aside class="side-panel" class:side-panel-branded={!!brandLogoUrl}>
      <div class="panel-head">
        <h2>{t('side.title')}</h2>
        {#if focused && !zoomedIn}
          <button class="reset-view" title={t('common.overview')} onclick={resetView}>
            <Icon name="reset" size={15} /><span>{t('common.overview')}</span>
          </button>
        {/if}
      </div>
      <PartList
        {items}
        {selectedId}
        {hiddenIds}
        bind:search={searchText}
        exact={scanExact}
        onSelect={selectFromList}
        onToggle={toggle}
        onToggleVisible={toggleVisible}
        onToggleModule={toggleModuleVisible}
        {groupSel}
        explodeActive={explodeOn}
        onToggleGroupSel={toggleGroupSel}
        onSelectAllGroups={selectAllGroups}
        onExplodeSel={toggleExplode}
        onHideSel={hideSelGroups}
        onIsolateSel={isolateSelGroups}
        onClearSel={clearSelection}
        labelsActive={labelsOn}
        onToggleLabels={toggleLabels}
        onScan={openScanner}
        onZoomSelected={toggleZoomSelected}
        onShowAll={showAll}
        canZoom={selectedId != null || groupSel.size > 0}
        {zoomedIn}
        hasHidden={hiddenIds.size > 0}
      />
      {#if selectedDetail}
        <InfoPanel info={selectedDetail} />
      {/if}
    </aside>
  {/if}

  {#if !embed && isMobile}
    <section class="sheet" class:open={sheetOpen}>
      <div class="sheet-handle">
        <span class="grab"></span>
        <button
          class="sheet-toggle"
          onclick={() => (sheetOpen = !sheetOpen)}
          aria-expanded={sheetOpen}
        >
          <span class="sheet-title">
            <strong>{doc.model.name}</strong>
            <small>{tn('parts.count', items.length)}</small>
          </span>
        </button>
        <!-- Zoom vào chi tiết đang chọn + Hiện lại toàn bộ: đặt ở tay cầm (luôn thấy cả khi sheet
             đóng) → không đặt trong PartList để tránh trùng khi sheet mở. -->
        <button
          class="handle-act"
          class:active={zoomedIn}
          title={zoomedIn ? t('common.overview') : t('common.zoomSelected')}
          aria-label={zoomedIn ? t('common.overview') : t('common.zoomSelected')}
          disabled={!zoomedIn && selectedId == null && groupSel.size === 0}
          onclick={toggleZoomSelected}
        >
          <Icon name={zoomedIn ? 'reset' : 'fit'} size={16} />
        </button>
        {#if hiddenIds.size > 0}
          <button
            class="handle-act"
            title={t('list.showAll')}
            aria-label={t('list.showAll')}
            onclick={showAll}
          >
            <Icon name="eye" size={16} />
          </button>
        {/if}
        {#if focused && !zoomedIn}
          <button class="reset-view" title={t('common.overview')} onclick={resetView}>
            <Icon name="reset" size={15} /><span>{t('common.overview')}</span>
          </button>
        {/if}
        <button
          class="sheet-chevron"
          onclick={() => (sheetOpen = !sheetOpen)}
          aria-label={t('side.title')}
          aria-expanded={sheetOpen}
        >
          <Icon name={sheetOpen ? 'chevronDown' : 'chevronUp'} />
        </button>
      </div>
      {#if !sheetOpen && selectedDetail}
        <div class="sheet-collapsed-info"><InfoPanel info={selectedDetail} /></div>
      {/if}
      {#if sheetOpen}
        <div class="sheet-body">
          <PartList
        {items}
        {selectedId}
        {hiddenIds}
        bind:search={searchText}
        exact={scanExact}
        onSelect={selectFromList}
        onToggle={toggle}
        onToggleVisible={toggleVisible}
        onToggleModule={toggleModuleVisible}
        {groupSel}
        explodeActive={explodeOn}
        onToggleGroupSel={toggleGroupSel}
        onSelectAllGroups={selectAllGroups}
        onExplodeSel={toggleExplode}
        onHideSel={hideSelGroups}
        onIsolateSel={isolateSelGroups}
        onClearSel={clearSelection}
        labelsActive={labelsOn}
        onToggleLabels={toggleLabels}
        onZoomSelected={toggleZoomSelected}
        canZoom={selectedId != null || groupSel.size > 0}
        {zoomedIn}
        onScan={openScanner}
      />
          {#if selectedDetail}
            <InfoPanel info={selectedDetail} />
          {/if}
        </div>
      {/if}
    </section>
  {/if}

  {#if isAuthor}
    <AuthorPanel
      selectedName={selectedDetail?.name ?? null}
      {selectedId}
      salonType={authSalon}
      flip={authFlip}
      swingOut={authSwingOut}
      currentGroup={authGroup}
      groups={motionGroups}
      {saving}
      {dirty}
      locked={blocked}
      {lighting}
      {lightsOn}
      {lightIntensity}
      onAssign={applyMotion}
      onClear={clearMotion}
      onIndependent={setIndependent}
      onMerge={mergeIntoGroup}
      onFlip={toggleFlip}
      onSwingOut={toggleSwingOut}
      onSave={save}
      onLighting={setLighting}
      onLightsToggle={toggleLights}
      onLightIntensity={setLightIntensity}
      onAiRender={() => (aiPanelOpen = true)}
    />
  {/if}

  {#if aiPanelOpen && token && authKey}
    <AiRenderPanel
      {token}
      {authKey}
      capture={captureHighRes}
      {captureGuide}
      onClose={() => (aiPanelOpen = false)}
      onToast={showToast}
      onSaved={refreshRenders}
    />
  {/if}

  {#if galleryOpen}
    <RenderGallery
      {renders}
      canDelete={isAuthor}
      onClose={() => (galleryOpen = false)}
      onDelete={deleteRenderFromGallery}
      onSetThumbnail={isAuthor ? setGalleryThumbnail : undefined}
    />
  {/if}

  {#if shareOpen && token}
    <ShareDialog
      {shareUrl}
      {token}
      {authKey}
      onCopy={(msg) => showToast(msg)}
      onClose={() => (shareOpen = false)}
    />
  {/if}

  {#if toastMsg}
    <Toast message={toastMsg} />
  {/if}

  {#if showHints && isMobile && !embed}
    <GestureHints onDismiss={dismissHints} />
  {/if}

  {#if ctxMenu && !embed}
    <ContextMenu
      x={ctxMenu.x}
      y={ctxMenu.y}
      title={ctxMenu.name}
      items={ctxItems}
      onClose={() => (ctxMenu = null)}
    />
  {/if}

  {#if scannerOpen && !embed}
    <QrScanner onResult={handleScanResult} onClose={() => (scannerOpen = false)} />
  {/if}
</div>

<style>
  .viewer {
    position: fixed;
    inset: 0;
    /* phông studio: gradient dịu A(sáng) → B(xanh lam nhạt sâu hơn) để model nổi, không lưới.
       Giữ đủ sáng cho UI nền kính + chữ tối tương phản. */
    background: linear-gradient(180deg, #eef4fc 0%, #b7cce6 100%);
  }
  canvas {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    touch-action: none;
  }
  .dim-overlay {
    pointer-events: none; /* không chặn OrbitControls */
    z-index: 10;
    touch-action: none;
  }

  /* WebGL không dựng được → phủ toàn màn, chặn tương tác, hướng dẫn user tự khắc phục */
  .webgl-fallback {
    position: absolute;
    inset: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-lg);
  }
  .webgl-card {
    max-width: 460px;
    text-align: center;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(8px);
    border-radius: 16px;
    padding: var(--space-xl) var(--space-lg);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    color: #334155;
  }
  .webgl-card :global(svg) {
    color: #d97706; /* amber cảnh báo */
  }
  .webgl-card h2 {
    margin: var(--space-md) 0 var(--space-sm);
    font-size: 1.15rem;
    color: #1e293b;
  }
  .webgl-card p {
    margin: 0 0 var(--space-lg);
    font-size: 0.92rem;
    line-height: 1.5;
  }
  .retry-btn {
    padding: 0.6em 1.6em;
    border: none;
    border-radius: 10px;
    background: #2563eb;
    color: #fff;
    font-size: 0.95rem;
    cursor: pointer;
  }
  .retry-btn:hover {
    background: #1d4ed8;
  }

  .brand {
    position: absolute;
    top: var(--space-md);
    left: var(--space-md);
    z-index: var(--z-toolbar);
  }
  .brand img {
    height: 50px;
    width: auto;
    max-width: 200px; /* guard: logo thương hiệu quá rộng không tràn ngang (11) */
    object-fit: contain; /* giữ tỉ lệ khi bị max-width kẹp */
    display: block;
  }
  .brand img.brand-custom {
    height: auto;
    min-height: 80px; /* logo thương hiệu designer: cao tối thiểu 80px để dễ nhận diện */
    max-height: 80px; /* chốt cùng min để chiều cao ổn định, tránh đè side-panel bên dưới */
  }

  .title-chip {
    position: absolute;
    top: var(--space-md);
    left: 50%;
    transform: translateX(-50%);
    z-index: var(--z-toolbar);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: var(--space-xs) var(--space-md);
    background: var(--color-surface-glass);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    box-shadow: var(--shadow-control);
    backdrop-filter: blur(8px);
  }
  .title-chip strong {
    font-size: var(--font-size-body);
  }
  .title-chip span {
    font-size: var(--font-size-caption);
    color: var(--color-text-secondary);
  }
  /* mobile: ẩn chip tên ở giữa (đè logo) — tên đã hiển thị trong bottom-sheet */
  @media (max-width: 767px) {
    .title-chip {
      display: none;
    }
  }

  .author-badge {
    position: absolute;
    top: 66px;
    left: 50%;
    transform: translateX(-50%);
    z-index: var(--z-toolbar);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: var(--radius-pill);
    background: var(--color-yellow-accent);
    color: var(--color-navy);
    font-size: var(--font-size-caption);
    font-weight: 700;
  }
  .author-badge.locked {
    background: var(--color-error);
    color: #fff;
  }

  /* nút "Về tổng thể": nằm trong header panel "Chi tiết mô hình" (desktop + mobile),
     hiện khi đang focus (tìm kiếm/quét QR/chọn) → 1 chạm về phối cảnh ban đầu. */
  .reset-view {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 30px;
    padding: 0 10px;
    border: 1px solid var(--color-primary);
    border-radius: var(--radius-pill);
    background: var(--color-surface-glass);
    color: var(--color-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
  }
  .reset-view:hover {
    background: var(--color-primary);
    color: #fff;
  }
  /* mobile: nút chỉ-icon ở tay cầm sheet (zoom vào chi tiết / hiện lại toàn bộ) */
  .handle-act {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 30px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-pill);
    background: var(--color-surface-glass);
    color: var(--color-primary);
    cursor: pointer;
    padding: 0;
  }
  .handle-act:disabled {
    color: var(--color-text-secondary);
    opacity: 0.4;
    cursor: default;
  }
  .handle-act.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  /* header panel desktop: tiêu đề + nút reset cùng hàng */
  .panel-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-sm);
  }

  .topright {
    position: absolute;
    top: var(--space-md);
    right: var(--space-md);
    /* cao hơn --z-panel (AuthorPanel) để dropdown ngôn ngữ không bị panel che (stacking context) */
    z-index: var(--z-modal);
    display: flex;
    align-items: center;
    gap: var(--space-xs);
  }
  .share-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: var(--size-touch-min);
    padding: 0 var(--space-md);
    border: 1px solid var(--color-primary);
    border-radius: var(--radius-control);
    background: var(--color-surface-glass);
    color: var(--color-primary);
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    backdrop-filter: blur(8px);
  }
  .share-btn:hover {
    background: var(--color-primary);
    color: #fff;
  }
  /* mobile: nút Share còn icon (text + dropdown ngôn ngữ trước đây đè logo trên màn hẹp) */
  @media (max-width: 767px) {
    .share-btn span {
      display: none;
    }
    .share-btn {
      width: var(--size-touch-min);
      padding: 0;
      justify-content: center;
    }
  }

  .embed-bar {
    position: absolute;
    right: var(--space-sm);
    bottom: var(--space-sm);
    z-index: var(--z-toolbar);
    display: flex;
    gap: 4px;
    padding: 4px;
    background: var(--color-surface-glass);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-pill);
    box-shadow: var(--shadow-control);
    backdrop-filter: blur(8px);
  }
  .embed-bar button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
  }
  .embed-bar button:hover {
    color: var(--color-primary);
    background: var(--color-canvas-bottom);
  }
  .embed-mark {
    position: absolute;
    left: var(--space-sm);
    bottom: var(--space-sm);
    z-index: var(--z-toolbar);
    opacity: 0.85;
  }
  .embed-mark img {
    height: 50px;
    display: block;
  }

  .side-panel {
    position: absolute;
    top: 72px; /* = space-md(16) + logo mặc định(50) + gap(6); xem .side-panel-branded khi có logo user */
    left: var(--space-md);
    bottom: 84px;
    width: var(--size-side-panel);
    z-index: var(--z-panel);
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
    padding: var(--space-md);
    background: var(--color-surface-glass);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
    backdrop-filter: blur(10px);
  }
  .side-panel-branded {
    top: 102px; /* space-md(16) + logo thương hiệu(80) + gap(6), tránh đè lên logo user */
  }
  .side-panel h2 {
    margin: 0;
    font-size: var(--font-size-body);
    font-weight: 700;
  }

  /* mobile bottom-sheet */
  .sheet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: var(--z-panel);
    background: var(--color-surface);
    border-top-left-radius: var(--radius-panel);
    border-top-right-radius: var(--radius-panel);
    box-shadow: 0 -8px 32px rgba(11, 31, 58, 0.16);
    max-height: 76vh;
    display: flex;
    flex-direction: column;
  }
  .sheet-handle {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: var(--space-xs) var(--space-md) var(--space-sm);
    position: relative;
  }
  .sheet-toggle {
    flex: 1;
    min-width: 0;
    display: flex;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    text-align: left;
  }
  .sheet-chevron {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: var(--size-touch-min);
    height: var(--size-touch-min);
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 0;
  }
  .grab {
    position: absolute;
    top: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: var(--color-border);
  }
  .sheet-title {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 8px;
    min-width: 0;
    max-width: 100%;
  }
  .sheet-title strong {
    font-size: var(--font-size-body);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sheet-title small {
    color: var(--color-text-secondary);
  }
  .sheet-body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
    padding: 0 var(--space-md) var(--space-md);
    overflow: hidden;
  }
  .sheet-collapsed-info {
    padding: 0 var(--space-md) var(--space-md);
  }
</style>
